import React, { useState } from "react";
import { 
  Avatar, 
  Box, 
  Menu, 
  MenuItem, 
  Typography, 
  useTheme, 
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { IconGradient, SmallTextGradient } from "../Gradient";
import { Modal } from "../Dialogs";
import { getGlassCardStyles } from "../../utility/glassCard.utils";
import { useAdminRole } from "../../hooks/useAdminRole";
import type { User as FirebaseUser } from "firebase/auth";

type UserProfile = { displayName?: string | null };
type Profile = { photoURL?: string | null } | null | undefined;

type UserMenuProps = {
  userId: string | undefined;
  user: UserProfile | null;
  profile: Profile;
  anchorEl: HTMLElement | null;
  open: boolean;
  handleMenuOpen: (e: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  handleLogout: () => void;
  setActive: (key: string) => void;
  authUser?: FirebaseUser | null;
};

const UserMenu: React.FC<UserMenuProps> = ({
  userId,
  user,
  profile,
  anchorEl,
  open,
  handleMenuOpen,
  handleMenuClose,
  handleLogout,
  setActive,
  authUser,
}) => {
  const theme = useTheme();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { isModerator } = useAdminRole(authUser || null);
  
  if (!userId) return null;

  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    handleLogout();
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <>
      <Box
        onClick={handleMenuOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          px: 1,
          py: 0.5,
          borderRadius: "999px",
          transition: "background 0.2s",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Tooltip title="Account Settings">
          <Avatar
            src={
              profile?.photoURL ||
              "https://cdn-icons-png.flaticon.com/512/149/149071.png"
            }
            alt={user.displayName || "Profile"}
            sx={{
              width: 36,
              height: 36,
              transition: "all 0.3s ease",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 0 0 2px #444"
                  : "0 0 0 2px #ccc",
              "&:hover": {
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
              },
            }}
          />
        </Tooltip>

        <Typography
          variant="body2"
          noWrap
          sx={{
            maxWidth: 120,
            fontWeight: 600,
            fontSize: "0.95rem",
            letterSpacing: 0.2,
            color: "text.primary",
          }}
        >
          {user.displayName}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          sx: (theme) => ({
            ...getGlassCardStyles(theme),
            p: 0,
            width: 'auto',
            borderRadius: 3,
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
          }}
        >
          <Avatar
            src={
              profile?.photoURL ||
              "https://cdn-icons-png.flaticon.com/512/149/149071.png"
            }
            alt={user.displayName || "Profile"}
            sx={{ width: 40, height: 40 }}
          />
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {user.displayName || "User"}
          </Typography>
        </Box>

        <MenuItem
          component={Link}
          to="/dashboard"
          onClick={() => {
            handleMenuClose();
            setActive("dashboard");
          }}
          sx={(theme) => ({
            px: 3,
            py: 1.25,
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.2s ease",
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            position: 'relative',
            "&:hover": {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              "& .menu-text-wrapper::after": {
                transform: "scaleX(1)",
              },
            },
            "& .menu-text-wrapper": {
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -2,
                height: "2px",
                background: theme.palette.mode === "dark"
                  ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                  : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                borderRadius: "1px",
                transform: "scaleX(0)",
                transformOrigin: "left center",
                transition: "transform 0.2s ease",
              },
            },
          })}
        >
          <IconGradient gradientVariant="default">
            <DashboardOutlinedIcon fontSize="small" />
          </IconGradient>
          <Box className="menu-text-wrapper">
            <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
              Dashboard
            </SmallTextGradient>
          </Box>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/my-stories"
          onClick={() => {
            handleMenuClose();
            setActive("mystories");
          }}
          sx={(theme) => ({
            px: 3,
            py: 1.25,
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.2s ease",
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            position: 'relative',
            "&:hover": {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              "& .menu-text-wrapper::after": {
                transform: "scaleX(1)",
              },
            },
            "& .menu-text-wrapper": {
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -2,
                height: "2px",
                background: theme.palette.mode === "dark"
                  ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                  : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                borderRadius: "1px",
                transform: "scaleX(0)",
                transformOrigin: "left center",
                transition: "transform 0.2s ease",
              },
            },
          })}
        >
          <IconGradient gradientVariant="default">
            <LibraryBooksOutlinedIcon fontSize="small" />
          </IconGradient>
          <Box className="menu-text-wrapper">
            <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
              My Stories
            </SmallTextGradient>
          </Box>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/my-bookmarks"
          onClick={() => {
            handleMenuClose();
            setActive("mybookmarks");
          }}
          sx={(theme) => ({
            px: 3,
            py: 1.25,
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.2s ease",
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            position: 'relative',
            "&:hover": {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              "& .menu-text-wrapper::after": {
                transform: "scaleX(1)",
              },
            },
            "& .menu-text-wrapper": {
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -2,
                height: "2px",
                background: theme.palette.mode === "dark"
                  ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                  : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                borderRadius: "1px",
                transform: "scaleX(0)",
                transformOrigin: "left center",
                transition: "transform 0.2s ease",
              },
            },
          })}
        >
          <IconGradient gradientVariant="default">
            <BookmarkIcon fontSize="small" />
          </IconGradient>
          <Box className="menu-text-wrapper">
            <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
              My Bookmarks
            </SmallTextGradient>
          </Box>
        </MenuItem>
        {isModerator && (
          <MenuItem
            component={Link}
            to="/moderation"
            onClick={() => {
              handleMenuClose();
              setActive("moderation");
            }}
            sx={(theme) => ({
              px: 3,
              py: 1.25,
              backgroundColor: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.2s ease",
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              position: 'relative',
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                "& .menu-text-wrapper::after": {
                  transform: "scaleX(1)",
                },
              },
              "& .menu-text-wrapper": {
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -2,
                  height: "2px",
                  background: theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                  borderRadius: "1px",
                  transform: "scaleX(0)",
                  transformOrigin: "left center",
                  transition: "transform 0.2s ease",
                },
              },
            })}
          >
            <IconGradient gradientVariant="default">
              <AdminPanelSettingsIcon fontSize="small" />
            </IconGradient>
            <Box className="menu-text-wrapper">
              <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                Moderation Queue
              </SmallTextGradient>
            </Box>
          </MenuItem>
        )}
        <MenuItem
          onClick={handleLogoutClick}
          sx={(theme) => ({
            px: 3,
            py: 1.25,
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.2s ease",
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            position: 'relative',
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(211,47,47,0.15)'
                  : 'rgba(211,47,47,0.1)',
                "& .menu-text-wrapper::after": {
                  transform: "scaleX(1)",
                },
                "& .MuiBox-root:first-of-type": { // Target the icon wrapper Box
                  "& svg": {
                    transform: "scale(1.1)",
                  },
                },
              },
            "& .menu-text-wrapper": {
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -2,
                height: "2px",
                background: "linear-gradient(90deg, #d32f2f 0%, #f44336 100%)",
                borderRadius: "1px",
                transform: "scaleX(0)",
                transformOrigin: "left center",
                transition: "transform 0.2s ease",
              },
            },
          })}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              "& svg": {
                fontSize: 18,
                color: theme.palette.error.main,
                transition: "transform 0.2s ease",
              },
            }}
          >
            <LogoutOutlinedIcon fontSize="small" />
          </Box>
          <Box className="menu-text-wrapper">
            <SmallTextGradient 
              variant="body2" 
              gradientVariant="default" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.error.main,
              }}
            >
              Logout
            </SmallTextGradient>
          </Box>
        </MenuItem>
      </Menu>

      {/* Logout Confirmation Dialog */}
      <Modal
        open={logoutDialogOpen}
        onClose={handleCancelLogout}
        onCancel={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        title="Ready to sign out?"
        description="We'll miss you! Sign in again whenever you're ready to continue your storytelling journey."
        icon={<WarningAmberRoundedIcon />}
        iconColor={theme.palette.error.main}
        cancelText="Cancel"
        confirmText="Logout"
        confirmButtonColor="error"
      />
    </>
  );
};

export default UserMenu;


